import { useEffect, useMemo, useState } from 'react';
import { useLocation, matchPath, PathMatch, generatePath } from 'react-router-dom';
import { RoutePath as rp } from 'src/router';

import { useSelector, useDispatch } from 'src/store';
import { setBreadcrumbsNames } from 'src/store/slices/breadcrumbsSlice';
import { breadcrumbsNamesMapSelector } from 'src/store/selectors/breadcrumbsSelector';

import { Breadcrumb, Breadcrumbs } from '@itm/shared-frontend/lib/components';

type BreadcrumbConfig = {
  name: string | null | undefined;
  parentPath: string | null;
};

type ExtendedBreadcrumbsConfig = BreadcrumbConfig & Pick<PathMatch<string>, 'pathname'>;

type BreadcrumbConfigMap = {
  [key: string]: BreadcrumbConfig;
};

type UseSetBreadcrumbsNamesProps = Readonly<{
  routePath: string;
  value: string | null | undefined;
  isClearOnUnmount?: boolean;
}>;

export const useSetBreadcrumbsNames = ({ routePath, value, isClearOnUnmount = true }: UseSetBreadcrumbsNamesProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!value) return;
    dispatch(setBreadcrumbsNames({ [routePath]: value }));
  }, [routePath, value, dispatch]);

  useEffect(
    () => () => {
      if (!isClearOnUnmount) return;
      dispatch(setBreadcrumbsNames({ [routePath]: null }));
    },
    [routePath, isClearOnUnmount, dispatch],
  );
};

const getBreadcrumbs = (pathToMatch: string, breadcrumbsConfig: BreadcrumbConfigMap): Breadcrumb[] => {
  const matchedList = Object.keys(breadcrumbsConfig).reduce<PathMatch<string>[]>((acc, route) => {
    const matched = matchPath(route, pathToMatch);
    return matched ? [...acc, matched] : acc;
  }, []);

  if (!matchedList.length) return [];

  const pathMatch =
    // try to find the full match
    matchedList.find((m) => !m.pattern.path.endsWith('/*')) ||
    // if not found, find the longest wildcard match
    matchedList.toSorted((a, b) => b.pattern.path.length - a.pattern.path.length)[0];

  const routeParams = pathMatch.params;

  let revertedBreadcrumbsList: Breadcrumb[] = [];
  let extendedConfig: ExtendedBreadcrumbsConfig | null = {
    pathname: pathMatch.pathname,
    ...breadcrumbsConfig[pathMatch.pattern.path],
  };

  while (extendedConfig) {
    const { name, pathname, parentPath }: ExtendedBreadcrumbsConfig = extendedConfig;

    if (name) {
      revertedBreadcrumbsList.push({ name, to: pathname });
    } else {
      revertedBreadcrumbsList = [];
    }

    extendedConfig = parentPath
      ? { ...breadcrumbsConfig[parentPath], pathname: generatePath(parentPath, routeParams) }
      : null;
  }
  return revertedBreadcrumbsList.reverse();
};

function BreadcrumbsTrail() {
  const { pathname } = useLocation();
  const bcNamesMap = useSelector(breadcrumbsNamesMapSelector);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const isHidden = useMemo(
    () =>
      Boolean(
        matchPath(`${rp.authRoot}/*`, pathname) ||
        matchPath(`${rp.dashboard}/*`, pathname) ||
        matchPath(rp.permissionDenied, pathname),
      ),
    [pathname],
  );

  const config: BreadcrumbConfigMap = useMemo(
    () => ({
      // DATA
      [rp.dataRoot]: { name: 'Data', parentPath: null },

      // Data / Data Access
      [rp.dataAccessRoot]: { name: 'Data Access Permission', parentPath: rp.dataRoot },

      // Data / Data Access / My Access
      [rp.dataAccessMyAccessList]: { name: 'My Access', parentPath: rp.dataAccessRoot },
      [rp.dataAccessMyAccessCreate]: { name: 'Request New Access', parentPath: rp.dataAccessMyAccessList },
      [rp.dataAccessMyAccessViewDetails]: {
        name: bcNamesMap[rp.dataAccessMyAccessViewDetails],
        parentPath: rp.dataAccessMyAccessList,
      },

      // Data / Data Access / Manage Access
      [rp.dataAccessManageRoot]: { name: 'Manage Access', parentPath: rp.dataRoot },
      [rp.dataAccessManagePendingRequests]: { name: 'Pending Requests', parentPath: rp.dataAccessManageRoot },
      [rp.dataAccessManagePendingRequestsViewDetails]: {
        name: bcNamesMap[rp.dataAccessManagePendingRequestsViewDetails],
        parentPath: rp.dataAccessManagePendingRequests,
      },
      [rp.dataAccessManageDataAccessDetails]: { name: 'Data Access Details', parentPath: rp.dataAccessManageRoot },
      [rp.dataAccessManageDataAccessDetailsViewDetails]: {
        name: bcNamesMap[rp.dataAccessManageDataAccessDetailsViewDetails],
        parentPath: rp.dataAccessManageDataAccessDetails,
      },

      // Data / Data Retention
      [rp.dataRetentionRoot]: { name: 'Data Retention', parentPath: rp.dataRoot },
      [`${rp.dataRetentionPolicyCreateRoot}/*`]: { name: 'Create new policy', parentPath: rp.dataRetentionRoot },
      [rp.dataRetentionPolicyList]: { name: 'All Policies', parentPath: rp.dataRetentionRoot },

      // Data / Data Retention / View Policy Details
      [rp.dataRetentionPolicyViewRoot]: {
        name: bcNamesMap[rp.dataRetentionPolicyViewRoot],
        parentPath: rp.dataRetentionPolicyList,
      },
      [rp.dataRetentionPolicyViewDetails]: { name: 'Policy Details', parentPath: rp.dataRetentionPolicyViewRoot },
      [rp.dataRetentionPolicyViewRules]: { name: 'Data Retention Rules', parentPath: rp.dataRetentionPolicyViewRoot },
      [`${rp.dataRetentionPolicyViewVersions}/*`]: {
        name: 'Version History',
        parentPath: rp.dataRetentionPolicyViewRoot,
      },

      // Data / Data Retention / Edit Policy
      [rp.dataRetentionPolicyEditRoot]: {
        name: bcNamesMap[rp.dataRetentionPolicyEditRoot],
        parentPath: rp.dataRetentionPolicyList,
      },
      [rp.dataRetentionPolicyEditDetails]: { name: 'Policy Details', parentPath: rp.dataRetentionPolicyEditRoot },
      [rp.dataRetentionPolicyEditRules]: { name: 'Data Retention Rules', parentPath: rp.dataRetentionPolicyEditRoot },
      [`${rp.dataRetentionPolicyEditVersions}/*`]: {
        name: 'Version History',
        parentPath: rp.dataRetentionPolicyEditRoot,
      },
      [rp.dataRetentionLogs]: { name: 'Data Retention Logs', parentPath: rp.dataRetentionRoot },

      // Data / Notifications
      [rp.dataNotificationsRoot]: { name: 'Notifications', parentPath: rp.dataRoot },
      [rp.dataNotificationsAllRoot]: { name: 'All Notifications', parentPath: rp.dataNotificationsRoot },
      [rp.dataNotificationsEmailLogsRoot]: { name: 'Email Logs', parentPath: rp.dataNotificationsRoot },
      [rp.dataNotificationsEmailLogsCompany]: { name: 'Company logs', parentPath: rp.dataNotificationsEmailLogsRoot },
      [rp.dataNotificationsEmailLogsAccount]: {
        name: 'Account Email Logs',
        parentPath: rp.dataNotificationsEmailLogsRoot,
      },
      // Data / Notifications / All / Default
      [rp.dataNotificationsAllDefaultSummary]: {
        name: 'Default Notifications',
        parentPath: rp.dataNotificationsAllRoot,
      },
      [rp.dataNotificationsAllDefaultProductTemplateList]: {
        name: bcNamesMap[rp.dataNotificationsAllDefaultProductTemplateList],
        parentPath: rp.dataNotificationsAllDefaultSummary,
      },
      // Data / Notifications / All / Default / View Template
      [rp.dataNotificationsAllDefaultProductTemplateViewRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllDefaultProductTemplateViewRoot],
        parentPath: rp.dataNotificationsAllDefaultProductTemplateList,
      },
      [rp.dataNotificationsAllDefaultProductTemplateViewRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllDefaultProductTemplateViewEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllDefaultProductTemplateViewAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateViewRoot,
      },
      // Data / Notifications / All / Default / Edit Template
      [rp.dataNotificationsAllDefaultProductTemplateEditRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllDefaultProductTemplateEditRoot],
        parentPath: rp.dataNotificationsAllDefaultProductTemplateList,
      },
      [rp.dataNotificationsAllDefaultProductTemplateEditRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllDefaultProductTemplateEditEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllDefaultProductTemplateEditAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllDefaultProductTemplateEditRoot,
      },
      // Data / Notifications / All / Tenant
      [rp.dataNotificationsAllTenantSummary]: { name: 'Tenant Notifications', parentPath: rp.dataNotificationsAllRoot },
      [rp.dataNotificationsAllTenantProductTemplateList]: {
        name: bcNamesMap[rp.dataNotificationsAllTenantProductTemplateList],
        parentPath: rp.dataNotificationsAllTenantSummary,
      },
      // Data / Notifications / All / Tenant / View Template
      [rp.dataNotificationsAllTenantProductTemplateViewRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllTenantProductTemplateViewRoot],
        parentPath: rp.dataNotificationsAllTenantProductTemplateList,
      },
      [rp.dataNotificationsAllTenantProductTemplateViewRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllTenantProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllTenantProductTemplateViewEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllTenantProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllTenantProductTemplateViewAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllTenantProductTemplateViewRoot,
      },
      // Data / Notifications / All / Tenant / Edit Template
      [rp.dataNotificationsAllTenantProductTemplateEditRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllTenantProductTemplateEditRoot],
        parentPath: rp.dataNotificationsAllTenantProductTemplateList,
      },
      [rp.dataNotificationsAllTenantProductTemplateEditRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllTenantProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllTenantProductTemplateEditEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllTenantProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllTenantProductTemplateEditAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllTenantProductTemplateEditRoot,
      },
      // Data / Notifications / All / Company
      [rp.dataNotificationsAllCompanySummary]: {
        name: 'Company Notifications',
        parentPath: rp.dataNotificationsAllRoot,
      },
      [rp.dataNotificationsAllCompanyProductTemplateList]: {
        name: bcNamesMap[rp.dataNotificationsAllCompanyProductTemplateList],
        parentPath: rp.dataNotificationsAllCompanySummary,
      },
      // Data / Notifications / All / Company / View Template
      [rp.dataNotificationsAllCompanyProductTemplateViewRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllCompanyProductTemplateViewRoot],
        parentPath: rp.dataNotificationsAllCompanyProductTemplateList,
      },
      [rp.dataNotificationsAllCompanyProductTemplateViewRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllCompanyProductTemplateViewEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateViewRoot,
      },
      [rp.dataNotificationsAllCompanyProductTemplateViewAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateViewRoot,
      },
      // Data / Notifications / All / Company / Edit Template
      [rp.dataNotificationsAllCompanyProductTemplateEditRoot]: {
        name: bcNamesMap[rp.dataNotificationsAllCompanyProductTemplateEditRoot],
        parentPath: rp.dataNotificationsAllCompanyProductTemplateList,
      },
      [rp.dataNotificationsAllCompanyProductTemplateEditRecipients]: {
        name: 'Recipients',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllCompanyProductTemplateEditEditor]: {
        name: 'Email content',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateEditRoot,
      },
      [rp.dataNotificationsAllCompanyProductTemplateEditAudit]: {
        name: 'Audit',
        parentPath: rp.dataNotificationsAllCompanyProductTemplateEditRoot,
      },

      // Data / Data Publish
      [rp.dataPublishRoot]: { name: 'Data Publish Configuration', parentPath: rp.dataRoot },
      [rp.dataPublishConfiguration]: { name: 'Configuration', parentPath: rp.dataPublishRoot },

      // ADMINISTRATION

      [rp.administrationRoot]: { name: 'Admin', parentPath: null },

      // Administration / Users
      [rp.administrationUserRoot]: { name: 'Users', parentPath: rp.administrationRoot },
      [`${rp.administrationUserCreateRoot}/*`]: { name: 'Invite New User', parentPath: rp.administrationUserRoot },
      [rp.administrationUserList]: { name: 'Manage Users', parentPath: rp.administrationUserRoot },

      [rp.administrationUserAuditList]: { name: 'Audit', parentPath: rp.administrationUserRoot },
      [rp.administrationUserAuditDetails]: {
        name: bcNamesMap[rp.administrationUserAuditDetails],
        parentPath: rp.administrationUserAuditList,
      },

      [rp.administrationUserUpdateRoot]: {
        name: bcNamesMap[rp.administrationUserUpdateRoot],
        parentPath: rp.administrationUserList,
      },
      [rp.administrationUserUpdateUserDetail]: { name: 'User Detail', parentPath: rp.administrationUserUpdateRoot },
      [rp.administrationUserUpdateAuthHistory]: {
        name: 'Authentication History',
        parentPath: rp.administrationUserUpdateRoot,
      },
      [rp.administrationUserUpdateSFTP]: { name: 'SFTP', parentPath: rp.administrationUserUpdateRoot },
      [rp.administrationUserUpdateAuditList]: { name: 'Audit', parentPath: rp.administrationUserUpdateRoot },
      [rp.administrationUserUpdateAuditDetails]: {
        name: bcNamesMap[rp.administrationUserUpdateAuditDetails],
        parentPath: rp.administrationUserUpdateAuditList,
      },
      [rp.administrationUserUpdateRolesAndPermissionsCompanyList]: {
        name: 'Roles and Permissions',
        parentPath: rp.administrationUserUpdateRoot,
      },
      [`${rp.administrationUserUpdateRolesAndPermissionsCompanyProductRoot}/*`]: {
        name: bcNamesMap[rp.administrationUserUpdateRolesAndPermissionsCompanyProductRoot],
        parentPath: rp.administrationUserUpdateRolesAndPermissionsCompanyList,
      },

      // Administration / Single sign-on
      [rp.administrationSsoRoot]: { name: 'Single Sign-On', parentPath: rp.administrationRoot },
      [rp.administrationSsoCreate]: { name: 'Add New Configuration', parentPath: rp.administrationSsoRoot },
      [rp.administrationSsoList]: { name: 'Manage Configuration', parentPath: rp.administrationSsoRoot },
      [rp.administrationSsoUpdate]: {
        name: bcNamesMap[rp.administrationSsoUpdate],
        parentPath: rp.administrationSsoList,
      },

      // Administration / PGP Encryption
      [rp.administrationPgpEncryptionRoot]: { name: 'PGP Encryption', parentPath: rp.administrationRoot },
      [rp.administrationPgpEncryptionList]: {
        name: 'Manage Key Configurations',
        parentPath: rp.administrationPgpEncryptionRoot,
      },
      [`${rp.administrationPgpEncryptionCreateRoot}/*`]: {
        name: 'Add New Key Configuration',
        parentPath: rp.administrationPgpEncryptionRoot,
      },
      // Administration / PGP Encryption / View
      [rp.administrationPgpEncryptionViewRoot]: {
        name: bcNamesMap[rp.administrationPgpEncryptionViewRoot],
        parentPath: rp.administrationPgpEncryptionList,
      },
      [rp.administrationPgpEncryptionViewDetails]: {
        name: 'General Details',
        parentPath: rp.administrationPgpEncryptionViewRoot,
      },
      [rp.administrationPgpEncryptionViewProducts]: {
        name: 'Products',
        parentPath: rp.administrationPgpEncryptionViewRoot,
      },
      // Administration / PGP Encryption / Update
      [rp.administrationPgpEncryptionUpdateRoot]: {
        name: bcNamesMap[rp.administrationPgpEncryptionUpdateRoot],
        parentPath: rp.administrationPgpEncryptionList,
      },
      [rp.administrationPgpEncryptionUpdateDetails]: {
        name: 'General Details',
        parentPath: rp.administrationPgpEncryptionUpdateRoot,
      },
      [rp.administrationPgpEncryptionUpdatePublicKey]: {
        name: 'Public Key',
        parentPath: rp.administrationPgpEncryptionUpdateRoot,
      },
      [rp.administrationPgpEncryptionUpdateProducts]: {
        name: 'Products',
        parentPath: rp.administrationPgpEncryptionUpdateRoot,
      },

      // Administration / Onboarding
      [rp.administrationOnboardingRoot]: { name: 'Onboarding', parentPath: rp.administrationRoot },

      // Administration / Onboarding / Company
      [`${rp.administrationOnboardingCompanyCreateRoot}/*`]: {
        name: 'Add New Company',
        parentPath: rp.administrationOnboardingRoot,
      },
      [rp.administrationOnboardingCompanyList]: {
        name: 'Manage Companies',
        parentPath: rp.administrationOnboardingRoot,
      },

      // Administration / Onboarding / Company / View company details
      [rp.administrationOnboardingCompanyViewRoot]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewRoot],
        parentPath: rp.administrationOnboardingCompanyList,
      },
      [rp.administrationOnboardingCompanyViewDetails]: {
        name: 'Company Details',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      // Administration / Onboarding / Company / View company details / Employers
      [rp.administrationOnboardingCompanyViewEmployersList]: {
        name: 'Employers',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      [rp.administrationOnboardingCompanyViewEmployersAdd]: {
        name: 'Add New Employer',
        parentPath: rp.administrationOnboardingCompanyViewEmployersList,
      },
      [rp.administrationOnboardingCompanyViewEmployersView]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewEmployersView],
        parentPath: rp.administrationOnboardingCompanyViewEmployersList,
      },
      // Administration / Onboarding / Company / View company details / Schemes
      [rp.administrationOnboardingCompanyViewCreateScheme]: {
        name: 'Add New Scheme',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeList]: {
        name: 'Schemes',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewRoot]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewSchemeViewRoot],
        parentPath: rp.administrationOnboardingCompanyViewSchemeList,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewDetails]: {
        name: 'Scheme Details',
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewSections]: {
        name: 'Benefit Sections',
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewSectionsView]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewSchemeViewSectionsView],
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewSections,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewSectionsCreate]: {
        name: 'Add new section',
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewStrategicPartners]: {
        name: 'Strategic Partners',
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewRoot,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewStrategicPartnersView]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewSchemeViewStrategicPartnersView],
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewStrategicPartners,
      },
      [rp.administrationOnboardingCompanyViewSchemeViewStrategicPartnersCreate]: {
        name: 'Add new partner',
        parentPath: rp.administrationOnboardingCompanyViewSchemeViewRoot,
      },
      // Administration / Onboarding / Company / View company details / Products
      [rp.administrationOnboardingCompanyViewProducts]: {
        name: 'Products',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      // Administration / Onboarding / Company / View company details / Scheme Access
      [`${rp.administrationOnboardingCompanyViewSchemeAccessRoot}/*`]: {
        name: 'Scheme Access',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      // Administration / Onboarding / Company / View company details / Audit
      [rp.administrationOnboardingCompanyViewAuditList]: {
        name: 'Audit',
        parentPath: rp.administrationOnboardingCompanyViewRoot,
      },
      [rp.administrationOnboardingCompanyViewAuditDetails]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyViewAuditDetails],
        parentPath: rp.administrationOnboardingCompanyViewAuditList,
      },

      // Administration / Onboarding / Company / Update company details
      [rp.administrationOnboardingCompanyUpdateRoot]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateRoot],
        parentPath: rp.administrationOnboardingCompanyList,
      },
      [rp.administrationOnboardingCompanyUpdateDetails]: {
        name: 'Company Details',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      // Administration / Onboarding / Company / Update company details / Employers
      [rp.administrationOnboardingCompanyUpdateEmployersList]: {
        name: 'Employers',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateEmployersAdd]: {
        name: 'Add New Employer',
        parentPath: rp.administrationOnboardingCompanyUpdateEmployersList,
      },
      [rp.administrationOnboardingCompanyUpdateEmployersUpdate]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateEmployersUpdate],
        parentPath: rp.administrationOnboardingCompanyUpdateEmployersList,
      },
      // Administration / Onboarding / Company / Update company details / Schemes
      [rp.administrationOnboardingCompanyUpdateCreateScheme]: {
        name: 'Add New Scheme',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeList]: {
        name: 'Schemes',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot],
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeList,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateDetails]: {
        name: 'Scheme Details',
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateStrategicPartners]: {
        name: 'Strategic Partners',
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateStrategicPartnersUpdate]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateSchemeUpdateStrategicPartnersUpdate],
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateStrategicPartners,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateStrategicPartnersCreate]: {
        name: 'Add new partner',
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateSections]: {
        name: 'Benefit Sections',
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateSectionsUpdate]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateSchemeUpdateSectionsUpdate],
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateSections,
      },
      [rp.administrationOnboardingCompanyUpdateSchemeUpdateSectionsCreate]: {
        name: 'Add new section',
        parentPath: rp.administrationOnboardingCompanyUpdateSchemeUpdateRoot,
      },
      // Administration / Onboarding / Company / Update company details / Products
      [rp.administrationOnboardingCompanyUpdateProducts]: {
        name: 'Products',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      // Administration / Onboarding / Company / Update company details / Scheme Access
      [`${rp.administrationOnboardingCompanyUpdateSchemeAccessRoot}/*`]: {
        name: 'Scheme Access',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      // Administration / Onboarding / Company / Update company details / Audit
      [rp.administrationOnboardingCompanyUpdateAuditList]: {
        name: 'Audit',
        parentPath: rp.administrationOnboardingCompanyUpdateRoot,
      },
      [rp.administrationOnboardingCompanyUpdateAuditDetails]: {
        name: bcNamesMap[rp.administrationOnboardingCompanyUpdateAuditDetails],
        parentPath: rp.administrationOnboardingCompanyUpdateAuditList,
      },

      // Administration / Onboarding / Bulk Onboard
      [rp.administrationOnboardingOnboardRoot]: {
        name: 'Bulk Onboard',
        parentPath: rp.administrationOnboardingRoot,
      },
      [rp.administrationOnboardingBulkUpload]: {
        name: bcNamesMap[rp.administrationOnboardingBulkUpload],
        parentPath: rp.administrationOnboardingOnboardRoot,
      },
      [rp.administrationOnboardingBulkUploadHistory]: {
        name: 'Upload History',
        parentPath: rp.administrationOnboardingOnboardRoot,
      },
      [rp.administrationOnboardingBulkUploadHistoryFile]: {
        name: bcNamesMap[rp.administrationOnboardingBulkUploadHistoryFile],
        parentPath: rp.administrationOnboardingBulkUploadHistory,
      },

      // Administration / Onboarding / Tenant Configuration
      [rp.administrationOnboardingTenantRoot]: {
        name: 'Tenant Configuration',
        parentPath: rp.administrationOnboardingRoot,
      },
      [rp.administrationOnboardingTenantList]: {
        name: 'Manage Tenant',
        parentPath: rp.administrationOnboardingTenantRoot,
      },
      [`${rp.administrationOnboardingTenantCreateRoot}/*`]: {
        name: 'Create New Tenant',
        parentPath: rp.administrationOnboardingTenantList,
      },
      [`${rp.administrationOnboardingTenantCreateRoot}/*`]: {
        name: 'Create New Tenant',
        parentPath: rp.administrationOnboardingTenantList,
      },
      [`${rp.administrationOnboardingTenantUpdateRoot}/*`]: {
        name: bcNamesMap[rp.administrationOnboardingTenantUpdateRoot],
        parentPath: rp.administrationOnboardingTenantList,
      },

      // Administration / API integration
      [rp.apiIntegrationsRoot]: { name: 'API Integrations', parentPath: rp.administrationRoot },
      [`${rp.apiIntegrationsCreateRoot}/*`]: {
        name: 'New API Integration to DataHub',
        parentPath: rp.apiIntegrationsRoot,
      },
      [rp.apiIntegrationsList]: { name: 'Manage Integrations', parentPath: rp.apiIntegrationsRoot },

      // Administration / API integration / View Details
      [rp.apiIntegrationsViewRoot]: {
        name: bcNamesMap[rp.apiIntegrationsViewRoot],
        parentPath: rp.apiIntegrationsList,
      },
      [rp.apiIntegrationsViewCredentials]: { name: 'API credentials', parentPath: rp.apiIntegrationsViewRoot },
      [rp.apiIntegrationsViewProducts]: { name: 'Products', parentPath: rp.apiIntegrationsViewRoot },

      // Administration / API integration / Update Details
      [rp.apiIntegrationsUpdateRoot]: {
        name: bcNamesMap[rp.apiIntegrationsUpdateRoot],
        parentPath: rp.apiIntegrationsList,
      },
      [rp.apiIntegrationsUpdateCredentials]: { name: 'API credentials', parentPath: rp.apiIntegrationsUpdateRoot },
      [rp.apiIntegrationsUpdateCredentialsIpAddress]: {
        name: bcNamesMap[rp.apiIntegrationsUpdateCredentialsIpAddress],
        parentPath: rp.apiIntegrationsUpdateCredentials,
      },
      [rp.apiIntegrationsUpdateProducts]: { name: 'Products', parentPath: rp.apiIntegrationsUpdateRoot },

      // Administration / API integration / Third Party Integrations
      [rp.apiIntegrationsThirdPartyList]: { name: 'Third Party Integrations ', parentPath: rp.apiIntegrationsRoot },
      [`${rp.apiIntegrationsThirdPartyCreate}/*`]: {
        name: 'New Third Party Integrations',
        parentPath: rp.apiIntegrationsThirdPartyList,
      },
      // Administration / API integration / Third Party Integrations / View Details
      [rp.apiIntegrationsThirdPartyViewDetails]: {
        name: bcNamesMap[rp.apiIntegrationsThirdPartyViewDetails],
        parentPath: rp.apiIntegrationsThirdPartyList,
      },
      // Administration / API integration / Third Party Integrations / Edit Details
      [rp.apiIntegrationsThirdPartyEditDetails]: {
        name: bcNamesMap[rp.apiIntegrationsThirdPartyEditDetails],
        parentPath: rp.apiIntegrationsThirdPartyList,
      },

      // Administration / Configuration Reports
      [rp.administrationReportsRoot]: { name: 'Configuration Reports', parentPath: rp.administrationRoot },
      [rp.administrationReportsGenerate]: { name: 'Generate Reports', parentPath: rp.administrationReportsRoot },
      [rp.administrationReportsHistoryList]: { name: 'Report History', parentPath: rp.administrationReportsRoot },
      [rp.administrationReportsHistoryDetails]: {
        name: bcNamesMap[rp.administrationReportsHistoryDetails],
        parentPath: rp.administrationReportsHistoryList,
      },
    }),
    [bcNamesMap],
  );

  // Debounce breadcrumbs update to avoid unnecessary updates by redirects
  useEffect(() => {
    if (isHidden) return;

    const delayTimeout = setTimeout(() => {
      setBreadcrumbs(getBreadcrumbs(pathname, config));
    }, 50);
    return () => {
      clearTimeout(delayTimeout);
    };
  }, [config, isHidden, pathname]);

  return isHidden ? null : <Breadcrumbs breadcrumbs={breadcrumbs} />;
}

export default BreadcrumbsTrail;
